import { applyLinkedPattern, createPatternClip, sanitizePattern } from './patternModel';
import { defaultTrackEffects } from '@/state/trackModel';

export const DOCK_NOTE_KEYS = ['a', 'w', 's', 'e', 'd', 'f', 't', 'g', 'y', 'h', 'u', 'j', 'k', 'o', 'l', 'p', ';'];
export const DOCK_NOTE_OFFSETS = Object.freeze({ a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12, o: 13, l: 14, p: 15, ';': 16 });
export const DRUM_MIDI_ROWS = Object.freeze({ 36: 'kick', 38: 'snare', 40: 'snare', 39: 'clap', 42: 'hat', 44: 'hat', 46: 'openhat', 37: 'rim', 49: 'crash', 45: 'perc', 47: 'perc', 48: 'perc', 50: 'perc' });
export const DRUM_KEY_ROWS = Object.freeze({ a: 'kick', s: 'snare', d: 'clap', f: 'hat', g: 'openhat', h: 'perc', j: 'rim', k: 'crash' });

export const isTextEntry = (target) => ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) || Boolean(target?.isContentEditable);

export function recordableTrackType(track) {
  const first = (track?.clips || []).filter((clip) => clip.isPattern && ['drums', 'keys', 'sampler'].includes(clip.patternType))
    .sort((a, b) => Number(a.start_bar || 0) - Number(b.start_bar || 0))[0];
  if (first) return first.patternType;
  if ((track?.clips || []).length) return null;
  if (track?.instrument === 'drums') return 'drums';
  if (track?.instrument === 'melody' || track?.instrument === 'bass') return 'keys';
  return null;
}

export function targetPatternClip(track, recordBar) {
  return (track?.clips || []).filter((clip) => clip.isPattern
    && Number(clip.start_bar || 0) <= recordBar
    && recordBar < Number(clip.start_bar || 0) + Number(clip.bars || clip.pattern?.patternBars || 1))
    .sort((a, b) => Number(b.start_bar || 0) - Number(a.start_bar || 0))[0] || null;
}

export function recordingClipBars(loopEnabled, loopRegion) {
  return Math.max(1, Math.min(8, loopEnabled ? Number(loopRegion?.endBar || 0) - Number(loopRegion?.startBar || 0) : 4));
}

function nearestSamplerPattern(track, recordBar) {
  return (track?.clips || []).filter((clip) => clip.isPattern && clip.patternType === 'sampler')
    .sort((a, b) => Math.abs(Number(a.start_bar || 0) - recordBar) - Math.abs(Number(b.start_bar || 0) - recordBar))[0] || null;
}

export function createRecordingClip({ type, track, recordBar, bars, kit = null, sampleDefaults = null, uuid = () => crypto.randomUUID() }) {
  const nearest = type === 'sampler' ? nearestSamplerPattern(track, recordBar) : null;
  const inherited = nearest ? { ...nearest.pattern, notes: [], chords: [], chordSource: null } : sampleDefaults;
  const clip = createPatternClip({ type, kit, sampleDefaults: inherited, startBar: recordBar, color: track?.color || '#9B5DE5' });
  const loopId = `pattern-${uuid()}`;
  const pattern = sanitizePattern(type, { ...clip.pattern, patternBars: bars });
  return { ...clip, id: `clip-${uuid()}`, loop_id: loopId, start_bar: recordBar, bars, naturalLoopBars: bars, pattern };
}

export function prepareRecordingTarget({ tracks, armedTrackId, recordBar, loopEnabled = false, loopRegion, kit = null, sampleDefaults = null, uuid = () => crypto.randomUUID() }) {
  let nextTracks = structuredClone(tracks || []);
  let track = nextTracks.find((item) => item.id === armedTrackId) || null;
  let createdTrack = false;
  if (!track) {
    const id = `track-${uuid()}`;
    track = { id, name: 'Keys', instrument: 'melody', color: '#9B5DE5', volume: 80, pan: 0, muted: false, solo: false,
      effects: defaultTrackEffects(), clips: [] };
    nextTracks.push(track); createdTrack = true;
  }
  const type = recordableTrackType(track) || 'keys';
  let clip = targetPatternClip(track, recordBar);
  let createdClip = false;
  if (!clip) {
    clip = createRecordingClip({ type, track, recordBar, bars: recordingClipBars(loopEnabled, loopRegion), kit, sampleDefaults, uuid });
    track.clips.push(clip); createdClip = true;
  }
  return { tracks: nextTracks, armedTrackId: track.id, trackId: track.id, type: clip.patternType, clipId: clip.id, loopId: clip.loop_id, clip, createdTrack, createdClip };
}

export function clipRelativeBeat({ contextTime, recordStart, beatSeconds, recordBar, clip }) {
  const absoluteBeat = recordBar * 4 + Math.max(0, (Number(contextTime) - Number(recordStart)) / Number(beatSeconds));
  const local = absoluteBeat - Number(clip.start_bar || 0) * 4 + Number(clip.offset || 0) * 4;
  const patternBeats = Number(clip.pattern?.patternBars || clip.bars || 1) * 4;
  return { beat: ((local % patternBeats) + patternBeats) % patternBeats, absoluteBeat, pastEnd: absoluteBeat >= (Number(clip.start_bar || 0) + Number(clip.bars || 1)) * 4 };
}

export function drumStepAtBeat(beat, stepsPerBar) {
  const grid = Number(stepsPerBar) === 12 ? 12 : 16;
  return Math.round(Number(beat) * grid / 4);
}

export function applyRecordingEvents(tracks, target, events, { autoQuantize = true, snap = '1/16' } = {}) {
  const clip = target.clip;
  let dropped = false;
  let nextPattern = sanitizePattern(target.type, clip.pattern);
  if (target.type === 'drums') {
    const steps = structuredClone(nextPattern.steps);
    for (const event of events || []) {
      if (event.pastEnd) { dropped = true; continue; }
      const row = event.row || DRUM_MIDI_ROWS[event.pitch];
      if (!row || !steps[row]) continue;
      const raw = drumStepAtBeat(event.beat, nextPattern.stepsPerBar);
      const index = Math.max(0, Math.min(steps[row].length - 1, raw));
      steps[row][index] = Math.max(steps[row][index] || 0, Number(event.velocity) >= 0.7 ? 1 : 0.55);
    }
    nextPattern = sanitizePattern('drums', { ...nextPattern, steps });
  } else {
    const fresh = [];
    const quantum = snap === '1/8' ? 0.5 : snap === '1/4' ? 1 : 0.25;
    for (const event of events || []) {
      if (event.pastEnd) { dropped = true; continue; }
      const start = autoQuantize === false ? event.beat : Math.round(event.beat / quantum) * quantum;
      fresh.push({ id: event.id, start: Math.min(nextPattern.patternBars * 4 - 1 / 64, Math.max(0, start)),
        length: Math.max(1 / 64, Number(event.length || 0.25)), pitch: Number(event.pitch), velocity: Number(event.velocity ?? 0.8) });
    }
    nextPattern = sanitizePattern(target.type, { ...nextPattern, notes: [...nextPattern.notes, ...fresh] });
  }
  const updated = applyLinkedPattern(tracks, target.loopId, target.type, nextPattern);
  const linkedCopies = updated.reduce((count, track) => count + track.clips.filter((item) => item.isPattern && item.loop_id === target.loopId).length, 0);
  return { tracks: updated, pattern: nextPattern, linkedCopies, dropped };
}

export const midiMessage = (data) => {
  const [status = 0, pitch = 0, rawVelocity = 0] = data || [];
  const kind = status & 0xf0;
  if (kind === 0x90 && rawVelocity > 0) return { type: 'on', pitch, velocity: rawVelocity / 127 };
  if (kind === 0x80 || (kind === 0x90 && rawVelocity === 0)) return { type: 'off', pitch, velocity: 0 };
  return null;
};
